/** P32 (Feb 27, 2026) — Date + units formatting helpers for the Rhinobill claim page. */

/** YYYY-MM-DD ISO string for today (local TZ). */
export const todayISO = () => new Date().toISOString().split('T')[0];

/** Day-of-week abbreviation for a YYYY-MM-DD string ("Mon", "Tue", ...). */
export function dayOfWeek(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  // Use noon to avoid TZ flips
  const dt = new Date(`${iso}T12:00:00`);
  return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US', { weekday: 'short' });
}

/** Format YYYY-MM-DD → "MM/DD/YYYY" for display (e.g. "2026-05-01" → "05/01/2026"). */
export function formatDateMDY(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

/** Convert units (15-min increments) to a "HH:MM" duration string ("4" → "01:00"). */
export function unitsToHHMM(units, unitMinutes = 15) {
  const u = Math.max(0, parseFloat(units) || 0);
  const minutes = Math.round(u * (parseInt(unitMinutes, 10) || 15));
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

/** Best-effort parser for the variety of date formats Rhinobill billers type.
 *  Accepts: ISO ("2026-05-01"), MM/DD/YYYY, MM/DD, just MM/DD (current year),
 *  single-day-of-month ("15" → current month), or natural shortcuts. Returns
 *  YYYY-MM-DD on success or null on failure. */
export function smartParseDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  // ISO (handles paste from API)
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const [_, y, mm, dd] = m;
    return `${y}-${String(parseInt(mm, 10)).padStart(2, '0')}-${String(parseInt(dd, 10)).padStart(2, '0')}`;
  }
  // MM/DD/YYYY  (M/D/YYYY also fine)
  m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (m) {
    const year = m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10);
    return `${year}-${String(parseInt(m[1], 10)).padStart(2, '0')}-${String(parseInt(m[2], 10)).padStart(2, '0')}`;
  }
  // MM/DD (assume current year)
  m = s.match(/^(\d{1,2})[/-](\d{1,2})$/);
  if (m) {
    const year = new Date().getFullYear();
    return `${year}-${String(parseInt(m[1], 10)).padStart(2, '0')}-${String(parseInt(m[2], 10)).padStart(2, '0')}`;
  }
  // Bare day-of-month (assume current month + year)
  m = s.match(/^(\d{1,2})$/);
  if (m) {
    const dt = new Date();
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(parseInt(m[1], 10)).padStart(2, '0')}`;
  }
  return null;
}
